import { avatarSrc } from "@/lib/defaults";
import { pausableInterval } from "@/lib/appVisibility";
import { resolveMediaUrl } from "@/components/FileUpload";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken, getDeviceId } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { useApp } from "@/context/AppContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, UserPlus, Heart, MessageCircle, Megaphone, CheckCheck, Radio, MoreHorizontal, Trash2, ChevronDown, Lock, Smartphone, AtSign, Repeat2 } from "lucide-react";
import { pushSupported, getPushSubscription, enablePush } from "@/lib/push";
import { requestNativePushPermission } from "@/lib/nativePush";
import { useIsMobile } from "@/lib/useIsMobile";
import { toast } from "sonner";
import { timeAgo } from "@/lib/timeAgo";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { groupNotifications, groupTitle } from "@/lib/notifGroup";
import { ModerationStatusModal } from "@/components/ModerationStatusModal";
import { modNotifText } from "@/lib/moderationTexts";
import { DONATION_RECEIVED_EVENT, thanksOverlayEnabled } from "@/components/DonationThanksOverlay";
import { celebrateDonationSent, DONOR_THANKS_DEFAULTS } from "@/lib/donationEvents";
import { FollowRequestNotifActions } from "@/components/privacy/ProfilePrivacy";

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");

const iconFor = (type) => ({ follow: UserPlus, reaction: Heart, comment: MessageCircle, admin: Megaphone, live: Radio, security: Lock, app_campaign: Smartphone, mention: AtSign, donation: Heart, donation_sent: Heart, repost: Repeat2 }[type] || Bell);

export const NotificationBell = () => {
  const { user, t, config, lang, refreshMe } = useApp();
  const brandLogo = config?.settings?.brand?.logo_url ? resolveMediaUrl(config.settings.brand.logo_url) : "/yalola-logo.png";
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [modModal, setModModal] = useState(null);
  const [tab, setTab] = useState("all");
  const [confirmClear, setConfirmClear] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // Real-time notifications over WebSocket (instant bell update; 30s polling stays as a fallback).
  useEffect(() => {
    if (!user) return undefined;
    let closed = false;
    let pingId = null;
    const connect = () => {
      if (closed) return;
      let ws;
      try { ws = new WebSocket(`${WS_BASE}/api/ws/notifications?token=${getToken()}`); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => { pingId = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000); };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          // Lot 554 : conversation lue → ses notifications « message » quittent la cloche (WhatsApp).
          if (msg.type === "notifications_removed") {
            const gone = new Set(msg.ids || []);
            setItems((prev) => prev.filter((x) => !gone.has(x.id)));
            if (typeof msg.unread === "number") setUnread(msg.unread);
            try { window.dispatchEvent(new CustomEvent("yalola:notif")); } catch { /* noop */ }
            return;
          }
          if (msg.type !== "notification") return;
          const n = msg.notification;
          // Notification réservée à d'autres appareils du membre (campagne « Mets à jour » ciblée par appareil) → ignorée ici.
          if (Array.isArray(n?.meta?.device_ids) && !n.meta.device_ids.includes(getDeviceId())) return;
          setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
          if (typeof msg.unread === "number") setUnread(msg.unread);
          // Identity / role decision: the account (identity_status, badges) changed server-side →
          // refresh it now so the Home reminder and the profile card update without a reload.
          if (n.type === "verification") {
            refreshMe().catch(() => {});
            try { window.dispatchEvent(new CustomEvent("yalola:identity_changed", { detail: n })); } catch { /* noop */ }
          }
          // Soutien confirmé → « Merci animé » (DonationThanksOverlay) à la place du toast générique, si activé dans l'admin.
          if (n.type === "donation" && n.meta?.kind === "donation_received" && thanksOverlayEnabled(config)) {
            try { window.dispatchEvent(new CustomEvent(DONATION_RECEIVED_EVENT, { detail: n })); } catch { /* noop */ }
            return;
          }
          // Mon propre soutien (feuille native) confirmé → fête SUR PLACE (une seule fois par paiement), pas de toast.
          if (n.type === "donation_sent") {
            const key = DONOR_THANKS_DEFAULTS[n.meta?.thanks_key] ? n.meta.thanks_key : "donate.sent_body";
            celebrateDonationSent(n.meta?.amount, n.meta?.currency, n.meta?.payment_intent || n.meta?.donation_id, n.meta?.thanks_custom || t(key, DONOR_THANKS_DEFAULTS[key]));
            return;
          }
          toast(n.title, {
            description: n.body || undefined,
            action: n.link
              ? {
                  label: n.type === "live" ? t("live.join", "Rejoindre") : t("notif.view", "Voir"),
                  onClick: () => {
                    api.post(`/notifications/${n.id}/read`).catch(() => {});
                    setUnread((u) => (n.read ? u : Math.max(0, u - 1)));
                    nav(n.link);
                  },
                }
              : undefined,
          });
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (pingId) clearInterval(pingId);
        if (!closed) reconnectRef.current = setTimeout(connect, 4000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => {
      closed = true;
      if (pingId) clearInterval(pingId);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Push notifications are mandatory (no on/off toggle). Silently ensure a
  // subscription exists when the browser already granted permission.
  useEffect(() => {
    if (!user || !pushSupported()) return;
    if (Notification.permission === "granted") {
      getPushSubscription().then((s) => { if (!s) enablePush().catch(() => {}); }).catch(() => {});
    }
  }, [user]);

  const load = useCallback(() => {
    if (!user) return;
    api.get(`/notifications?tab=${tab}`)
      .then(({ data }) => { setItems(data.notifications || []); setUnread(data.unread || 0); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab, lang]); // lang: texts are re-rendered server-side in the current UI language

  useEffect(() => {
    const cancelFirst = whenFeedReady(load); // la liste attend le 1er rendu du fil (le compteur vient de nav-badges)
    const stop = pausableInterval(load, 30000); // lot 699 — en pause quand l'app est cachée, rafraîchie au retour
    return () => { stop(); cancelFirst(); };
  }, [load]);

  const isMobile = useIsMobile();
  const askPush = () => {
    // On the first open, request push permission (valid user gesture). Web → browser permission ;
    // app iOS → contexte naturel de la demande système (reportée si ATT/UMP viennent de se fermer).
    if (pushSupported() && Notification.permission === "default") enablePush().catch(() => {});
    requestNativePushPermission().catch(() => {});
  };
  const onOpen = (o) => {
    setOpen(o);
    if (o) askPush();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      if (tab === "unread") load();
    } catch { /* ignore */ }
  };

  const deleteOne = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      if (!n.read) setUnread((u) => Math.max(0, u - 1));
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  const clearAll = async () => {
    setConfirmClear(false);
    if (items.length === 0) return;
    try {
      await api.delete(`/notifications?tab=${tab}`);
      setItems([]);
      if (tab !== "read") setUnread(0);
      toast.success(t("notif.cleared", "Notifications vidées"));
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  const clearConfirmText = tab === "unread"
    ? t("notif.clear_confirm_unread", "Vider les notifications non lues ?")
    : tab === "read"
      ? t("notif.clear_confirm_read", "Vider les notifications lues ?")
      : t("notif.clear_confirm_all", "Vider toutes les notifications ?");

  const click = (n) => {
    api.post(`/notifications/${n.id}/read`).catch(() => {});
    setUnread((u) => (n.read ? u : Math.max(0, u - 1)));
    setOpen(false);
    // Moderation notifications open the "content under review" modal (live status,
    // clear reason, appeal) instead of navigating away.
    const mod = n.meta?.moderation || n.meta?.appeal;
    if (n.type === "moderation" && mod?.target_type && mod?.target_id) { setModModal(mod); return; }
    if (n.link) nav(n.link);
    else toast(n.title, { description: t("notif.no_target", "Cette notification n'a pas d'action associée.") });
  };

  const toggleGroup = (id) => setExpandedGroups((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const deleteGroup = async (row) => {
    const ids = row.items.map((x) => x.id);
    const unreadCount = row.items.filter((x) => !x.read).length;
    setItems((cur) => cur.filter((x) => !ids.includes(x.id)));
    if (unreadCount) setUnread((u) => Math.max(0, u - unreadCount));
    try { await Promise.all(ids.map((id) => api.delete(`/notifications/${id}`))); } catch { /* keep optimistic */ }
  };

  const renderItem = (n) => (
    <div
      key={n.id}
      role="button"
      tabIndex={0}
      onClick={() => click(n)}
      onKeyDown={(e) => { if (e.key === "Enter") click(n); }}
      className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:brightness-[0.98] cursor-pointer"
      style={{ background: n.read ? "transparent" : "var(--yalola-primary-soft)" }}
      data-testid={`notif-item-${n.id}`}
    >
      {n.actor ? (
        <Avatar className="h-9 w-9 shrink-0"><AvatarImage src={avatarSrc(n.actor)} /><AvatarFallback>{n.actor.name?.[0]}</AvatarFallback></Avatar>
      ) : (
        <div className="h-9 w-9 shrink-0 rounded-full overflow-hidden grid place-items-center" style={{ background: "var(--yalola-primary)" }}>
          <img src={brandLogo} alt="Yalola" className="h-full w-full object-cover" data-testid={`notif-brand-${n.id}`} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm leading-snug font-semibold">{modNotifText(n, t)?.title || n.title}</div>
        {(modNotifText(n, t)?.body || n.body) && <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{modNotifText(n, t)?.body || n.body}</div>}
        <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(n.created_at, t)}</div>
        <FollowRequestNotifActions n={n} testPrefix="bell" onDone={(patch) => {
          setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true, meta: { ...x.meta, ...patch } } : x)));
          setUnread((u) => (n.read ? u : Math.max(0, u - 1)));
        }} />
        {n.type === "live" && n.link && (
          <button
            onClick={(e) => { e.stopPropagation(); api.post(`/notifications/${n.id}/read`).catch(() => {}); setUnread((u) => (n.read ? u : Math.max(0, u - 1))); setOpen(false); nav(n.link); }}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-white px-3 py-1.5 rounded-full transition-transform active:scale-95"
            style={{ background: "#DC2626" }}
            data-testid={`notif-join-${n.id}`}
          >
            <Radio size={12} /> {t("live.join", "Rejoindre")}
          </button>
        )}
      </div>
      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: "var(--yalola-primary)" }} />}
      {n.image && <img src={resolveMediaUrl(n.image)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" data-testid={`notif-image-${n.id}`} />}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 -mt-0.5 -mr-1.5 p-1.5 rounded-full opacity-50 hover:opacity-100 hover:bg-black/5 transition"
            title={t("notif.delete", "Supprimer")}
            data-testid={`notif-menu-${n.id}`}
          >
            <MoreHorizontal size={16} style={{ color: "var(--yalola-text-muted)" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onClick={(e) => { e.stopPropagation(); deleteOne(n); }}
            className="text-red-600 focus:text-red-600 cursor-pointer"
            data-testid={`notif-delete-${n.id}`}
          >
            <Trash2 size={15} className="mr-2" /> {t("notif.delete", "Supprimer")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  if (!user) return null;

  // Téléphone/tablette : la cloche ouvre la page Notifications plein écran (façon Facebook/Instagram) —
  // plus de panneau flottant à refermer. Le PC garde le panneau.
  if (isMobile) {
    return (
      <>
        <button className="tab-icon relative" onClick={() => { askPush(); nav("/notifications"); }} aria-label={t("notif.title", "Notifications")} data-testid="header-notifications">
          <Bell size={19} />
          {unread > 0 && (
            <span className="absolute top-1.5 right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white grid place-items-center" style={{ background: "var(--yalola-primary)" }} data-testid="notif-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
        <ModerationStatusModal open={!!modModal} meta={modModal} onClose={() => setModModal(null)} />
      </>
    );
  }

  return (
    <>
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="tab-icon relative" data-testid="header-notifications">
          <Bell size={19} />
          {unread > 0 && (
            <span className="absolute top-1.5 right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white grid place-items-center" style={{ background: "var(--yalola-primary)" }} data-testid="notif-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="font-bold font-display">{t("notif.title", "Notifications")}</div>
          <div className="flex items-center gap-3">
            <button onClick={markAllRead} title={t("notif.mark_all_read", "Tout marquer comme lu")} className="opacity-70 hover:opacity-100" data-testid="notif-mark-all-read">
              <CheckCheck size={16} style={{ color: "var(--yalola-text-muted)" }} />
            </button>
            {items.length > 0 && (
              <button onClick={() => setConfirmClear(true)} title={t("notif.clear_all", "Tout vider")} className="opacity-70 hover:opacity-100" data-testid="notif-clear-all">
                <Trash2 size={16} style={{ color: "var(--yalola-text-muted)" }} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 border-b overflow-x-auto hide-scrollbar" style={{ borderColor: "var(--yalola-border)" }}>
          {[["all", t("notif.all", "Tout")], ["unread", `${t("notif.unread", "Non lu")}${unread > 0 ? ` (${unread})` : ""}`], ["read", t("notif.read", "Lu")], ["mentions", t("notif.mentions", "Mentions")]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className="px-3 py-1 rounded-full text-xs font-semibold transition-colors shrink-0"
              style={tab === key
                ? { background: "var(--yalola-primary)", color: "#fff" }
                : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}
              data-testid={`notif-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 && (
            <div className="p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="notif-empty">
              {tab === "unread"
                ? t("notif.empty_unread", "Aucune notification non lue")
                : tab === "read"
                  ? t("notif.empty_read", "Aucune notification lue")
                  : tab === "mentions"
                    ? t("notif.empty_mentions", "Personne ne t'a encore mentionné")
                    : t("notif.empty", "Aucune notification pour le moment")}
            </div>
          )}
          {groupNotifications(items).map((row) => {
            if (row.__group && !expandedGroups.has(row.id)) {
              const anyUnread = row.items.some((x) => !x.read);
              const GIcon = iconFor(row.type);
              return (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleGroup(row.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") toggleGroup(row.id); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:brightness-[0.98] cursor-pointer"
                  style={{ background: anyUnread ? "var(--yalola-primary-soft)" : "transparent" }}
                  data-testid={`notif-group-${row.type}`}
                >
                  <div className="h-9 w-9 shrink-0 rounded-full grid place-items-center" style={{ background: "var(--yalola-primary)" }}>
                    <GIcon size={16} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm leading-snug font-semibold">{groupTitle(row.type, row.items.length, t)}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(row.items[0].created_at, t)}</div>
                  </div>
                  {anyUnread && <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "var(--yalola-primary)" }} />}
                  <ChevronDown size={16} className="shrink-0" style={{ color: "var(--yalola-text-muted)" }} />
                  <button onClick={(e) => { e.stopPropagation(); deleteGroup(row); }} className="shrink-0 -mr-1.5 p-1.5 rounded-full opacity-50 hover:opacity-100 hover:bg-black/5 transition" title={t("notif.delete", "Supprimer")} data-testid={`notif-group-delete-${row.type}`}>
                    <Trash2 size={15} style={{ color: "var(--yalola-text-muted)" }} />
                  </button>
                </div>
              );
            }
            return (row.__group ? row.items : [row]).map((n) => renderItem(n));
          })}
        </div>

        <button
          onClick={() => { setOpen(false); nav("/notifications"); }}
          className="w-full text-center py-3 text-sm font-semibold border-t hover:brightness-95 transition"
          style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-primary)" }}
          data-testid="notif-see-all"
        >
          {t("notif.see_all", "Voir toutes les notifications")}
        </button>
        <ConfirmDialog
          open={confirmClear}
          title={clearConfirmText}
          description={t("notif.clear_confirm_desc", "Cette action est définitive.")}
          confirmLabel={t("notif.clear", "Vider")}
          onConfirm={clearAll}
          onCancel={() => setConfirmClear(false)}
          testId="notif-clear-confirm"
        />
      </PopoverContent>
    </Popover>
      <ModerationStatusModal open={!!modModal} meta={modModal} onClose={() => setModModal(null)} />
    </>
  );
};
